import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { Toolbar, AppBar, Tab, Tabs, Typography, Box, Tooltip, IconButton, Chip, Stack } from '@mui/material';
import { QuestionAnswer, KeyboardBackspace, Add, Edit, Poll } from '@mui/icons-material';
import { format } from 'date-fns';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import useApi from '../hooks/useApi';
import useSnackbar from '../hooks/useSnackbar';
import { useTheme } from '@mui/material/styles';
import CustomChip from './CustomChip';
import TopicsList from './TopicsList';
import SurveyResultsCharts from './views/SurveyResultsCharts';
import SurveyResultsTable from './views/SurveyResultsTable';
import SurveyInsights from './views/SurveyInsights';
import SurveyChannels from './views/SurveyChannels';
import decodeSurveyData from './surveys/decodeSurveyData';

const titleStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
};

const Survey = () => {
    const theme = useTheme();
    const { id } = useParams();
    const { handleGet } = useApi();
    const { showSnackbar } = useSnackbar();
    const [survey, setSurvey] = useState(null);
    const [parsedSurvey, setParsedSurvey] = useState(null);
    const [parsedResults, setParsedResults] = useState(null);
    const [actions, setActions] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialising, setIsInitialising] = useState(true);
    const [tab, setTab] = useState('charts');
    const [answers, setAnswers] = useState(null);
    const [questions, setQuestions] = useState(null);
    const [questionComments, setQuestionComments] = useState(null);
    const [channels, setChannels] = useState(null);

    const surveyTitle = parsedSurvey?.title ?? 'Untitled Survey';

    const exportFileName = `${surveyTitle.toLowerCase().replace(/[^a-zA-Z0-9]/g, '')}-${format(Date.now(), 'yyyy-MM-dd-hh-mm-ss') }`;

    const handleLoadReport = async () => {
        const response = await handleGet(`surveys/reports/${id}`);

        if (!response.ok) {
            showSnackbar('Error fetching Report', 'error');
            return;
        }

        setSurvey(await response.json());
    };

    const handleFetchChannelData = async () => {
        const response = await handleGet(`surveys/reports/${id}/channels`);

        if (!response.ok) {
            showSnackbar('Error fetching Channel Data', 'error');
            return;
        }

        setChannels(await response.json());
    };

    const handleLoadActions = async () => {
        const response = await handleGet(`surveys/reports/${id}/actions`);

        if (!response.ok) {
            showSnackbar('Error fetching Actions', 'error');
            return;
        }

        setActions(await response.json());
    };

    useEffect(() => {
        if (!isInitialising) {
            setIsLoading(false);
            setSurvey(null);
        }

        handleLoadReport();
        handleLoadActions();
        handleFetchChannelData();
    }, [id]);

    useEffect(() => {
        if (survey) {
            setParsedSurvey(JSON.parse(survey.json));

            const results = survey.results.map(e => JSON.parse(e.result));

            setParsedResults(results);
        }
    }, [survey]);

    useEffect(() => {
        if (parsedResults) {
            const { answers, questionComments, questions } = decodeSurveyData({ parsedResults, parsedSurvey });

            setAnswers(answers);
            setQuestions(questions);
            setQuestionComments(questionComments);
        }
    }, [parsedResults]);

    useEffect(() => {
        setIsLoading(!Boolean(survey));

        if (!survey) {
            return;
        }
        else {
            setIsInitialising(false);
        }
    }, [survey]);

    if (isLoading || isInitialising || !survey) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            <AppBar
                position="sticky"
                color="inherit"
                sx={{
                    top: 120,
                    zIndex: theme.zIndex.appBar - 1,
                    borderTop: 1,
                    borderColor: theme.palette.divider
                }}
            >
                <Toolbar variant="dense" sx={{ overflow: 'hidden', pt: 2, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                        <Tooltip title="Return to Dashboard">
                            <IconButton
                                component={Link}
                                to="/newreports/surveys"
                            >
                                <KeyboardBackspace fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', pl: 1 }}>
                            <CustomChip
                                label={survey.name}
                                icon={<Poll fontSize="inherit" />}
                            />
                            <CustomChip
                                label={`Created ${format((new Date(survey.created)), 'dd-MM-yyyy HH:mm')}`}
                                icon={<Add fontSize="inherit" />}
                            />
                            <CustomChip
                                label={`Modified ${format((new Date(survey.modified)), 'dd-MM-yyyy HH:mm')}`}
                                icon={<Edit fontSize="inherit" />}
                            />
                            <CustomChip
                                label={`${survey.resultsCount.toLocaleString()} Response${survey.resultsCount === 1 ? '' : 's'}`}
                                icon={<QuestionAnswer fontSize="inherit" />}
                            />
                            <TopicsList topics={survey.topics} />
                            <Chip
                                component="span"
                                color={survey.status === 'Active' ? 'success' : undefined}
                                label={survey.status}
                                size="small"
                            />
                        </Stack>
                    </Box>
                </Toolbar>
                <Toolbar variant="dense">
                    <Stack>
                        <Typography
                            component="h1"
                            variant="h6"
                            sx={titleStyle}
                        >
                            {surveyTitle}
                        </Typography>
                        {parsedSurvey?.description && (
                            <Typography
                                component="h2"
                                variant="body1"
                                sx={titleStyle}
                            >
                                {parsedSurvey.description}
                            </Typography>
                        )}
                    </Stack>
                </Toolbar>
                <Tabs value={tab} onChange={(e, tab) => setTab(tab)}>
                    <Tab value="charts" label="Results Charts" />
                    <Tab value="table" label="Results Table" />
                    <Tab value="insights" label="Insights" />
                    <Tab value="channels" label="Channels" disabled={channels?.emails.length === 0 && channels?.qrCodes.length === 0 && channels?.teamsCampaigns.length === 0} />
                </Tabs>
            </AppBar>
            <Box sx={{ overflow: 'hidden', p: 2 }}>
                {tab === 'charts' && (
                    <SurveyResultsCharts
                        pages={parsedSurvey.pages}
                        questions={questions}
                        answers={answers}
                        comments={questionComments}
                    />
                )}
                {tab === 'table' && (
                    <SurveyResultsTable
                        questions={questions}
                        answers={answers}
                        exportFileName={exportFileName}
                    />
                )}
                {tab === 'insights' && (
                    <SurveyInsights
                        survey={survey}
                        actions={actions}
                        pages={parsedSurvey.pages}
                        questions={questions}
                        answers={answers}
                        comments={questionComments}
                        onSetTab={setTab}
                        channels={channels}
                    />
                )}
                {tab === 'channels' && (
                    <SurveyChannels channels={channels} />
                )}
            </Box>
        </>
    );
};

export default Survey;